import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import type { ApiResponse } from '../types';
import { useTenantStore } from '../stores/tenantStore';
import { USER_TYPES, API_ERROR_CODES, STORAGE_KEYS } from '../utils/constants';
import storage from '../utils/storage';
import { toSessionUser } from '../utils/sessionUser';

// VSNP-704: single-flight silent refresh. If a request 401s with a token
// still in the refresh window, retry once after rotating. Multiple
// concurrent 401s share one refresh attempt.
let inflightRefresh: Promise<string | null> | null = null;

async function attemptSilentRefresh(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      const currentToken = useAuthStore.getState().getVSNToken();
      if (!currentToken) return null;
      const resp = await axios.post(
        `${baseURL}/auth/refresh`,
        {},
        { headers: { Authorization: `Bearer ${currentToken}` } },
      );
      // The /auth/refresh endpoint returns the v1 envelope (`{ data: {...} }`).
      const body = resp.data?.data ?? resp.data;
      if (body?.token) {
        // Map the wire user (`userType`, no `channelIds`) to the web model, and
        // carry channelIds forward from the current user — refresh omits them.
        // Storing body.user raw here left a ChannelAdmin with `type: undefined`
        // after the first refresh, dropping their tenant picker and role label.
        const prevUser = useAuthStore.getState().user;
        const user = toSessionUser(body.user, prevUser);
        useAuthStore.getState().setVSNAuth(body.token, body.expiresAt, user, body.absoluteExpiresAt);
        return body.token as string;
      }
      return null;
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
  timeout: 15000,
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Handle /api/v2/ routes - they should not use the /api/v1 baseURL
    if (config.url && config.url.startsWith('/api/v2/')) {
      config.baseURL = '';
    }

    const { getAccessToken, user } = useAuthStore.getState();
    const accessToken = getAccessToken();

    // Add authorization header if token available
    // Only use the standard Authorization header - avoid non-standard headers
    if (accessToken) {
      config.headers['Authorization'] = accessToken.startsWith('Bearer ')
        ? accessToken
        : `Bearer ${accessToken}`;
    }

    // Add language header
    const language = storage.session.get<string>(STORAGE_KEYS.LANGUAGE) || 'en-US';
    config.headers['User-Language'] = language;

    // Inject tenantId for users with no tenant of their own (Global Filter).
    // GET: always inject. POST/PUT/DELETE: inject for routes that scope to a specific tenant
    // (DNS mutations, edge peering topology management, etc.) so newly-created resources
    // land on the selected tenant, not the SuperAdmin's 'default' placeholder tenant.
    //
    // Both SuperAdmin (type 1) and ChannelAdmin (type 5) have `tenantId: null` and pick a
    // tenant from the topbar, so both need the injection. They differ only in which tenants
    // they may pick, and that set is constrained server-side (GET /tenants/select is already
    // channel-scoped) — the client never has to know the set, and must not treat it as the
    // security boundary.
    const mutationTenantScopedRoutes = ['/dns/', '/api/v2/peering', '/api/v2/wireguard/apps/ikev2', '/email-templates'];
    const injectTenant =
      config.method === 'get' ||
      mutationTenantScopedRoutes.some((path) => config.url?.startsWith(path));
    const isGlobalScopedUser =
      user?.type === USER_TYPES.SUPER_ADMIN || user?.type === USER_TYPES.CHANNEL_ADMIN;
    if (injectTenant && isGlobalScopedUser) {
      const selectedTenantId = useTenantStore.getState().getTenantId();

      // Exclude admin/utility routes from global filtering
      const excludedPaths = [
        '/tenants',
        // Channels are a scope-management route, not a tenant-scoped resource —
        // filtering them by the selected tenant is meaningless.
        '/channels',
        '/users',
        '/roles',
        '/companies',
        '/users/me',
        '/permission/',
        '/api/v2/edges/pending',
        '/system',
        '/activity',
      ];

      const isExcluded = excludedPaths.some(path => config.url?.startsWith(path));

      if (selectedTenantId && !isExcluded && !config.params?.tenantId) {
        config.params = {
          ...config.params,
          tenantId: selectedTenantId
        };
      }
    }

    // Show loading indicator unless explicitly disabled
    const showLoading = (config as AxiosRequestConfig & { showLoading?: boolean }).showLoading;
    if (showLoading !== false) {
      useUIStore.getState().showLoading();
    }

    return config;
  },

  (error) => {
    useUIStore.getState().hideLoading();
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    useUIStore.getState().hideLoading();

    const data = response.data as ApiResponse;

    // Only check for error codes if the response has a code field (v1 API format)
    // v2 API responses don't have a code field and return data directly
    if (data && typeof data === 'object' && 'code' in data) {
      // Handle token errors
      if (data.code === API_ERROR_CODES.TOKEN_ERROR_1 || data.code === API_ERROR_CODES.TOKEN_ERROR_2) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(new Error('Token error'));
      }

      // Handle forbidden — let the calling component handle (toast)
      if (data.code === API_ERROR_CODES.FORBIDDEN) {
        return Promise.reject(new Error(data.message || 'Permission denied'));
      }
    }

    return response.data;
  },
  async (error) => {
    useUIStore.getState().hideLoading();

    // Handle 403 Forbidden — let the calling component handle (toast)
    if (error.response?.status === 403) {
      const msg = error.response?.data?.error?.message || error.response?.data?.message || 'Permission denied';
      return Promise.reject(new Error(msg));
    }

    // Handle 401 Unauthorized — drop the session and bounce to /login
    if (error.response?.status === API_ERROR_CODES.UNAUTHORIZED) {
      // Public auth endpoints handle their own 401s (login failure, invalid token)
      // — don't force a logout/redirect when the user isn't logged in yet.
      const requestUrl = error.config?.url || '';
      const publicAuthPaths = [
        '/auth/login',
        '/auth/validate-invite',
        '/auth/accept-invite',
        '/auth/password-policy',
        '/auth/resend-verification',
        '/auth/verify-email',
        '/auth/validate-reset-token',
        '/auth/reset-password',
        '/auth/refresh', // refresh failure → fall through to logout, don't loop
      ];
      const isPublicAuthCall = publicAuthPaths.some((p) => requestUrl.includes(p));

      // Don't bounce if we're already on a public auth page
      const isOnPublicAuthPage =
        window.location.pathname === '/login' ||
        window.location.pathname === '/accept-invite' ||
        window.location.pathname === '/email-verified' ||
        window.location.pathname === '/reset-password';

      // VSNP-704: try a single silent refresh before bouncing. Backend's
      // /auth/refresh accepts any valid token and rotates only when
      // inside the refresh window, so this is cheap to attempt and
      // recovers transient sessions (e.g. tab woken from background
      // right as the access token expired). Skip for refresh itself
      // (would loop) and for public-auth endpoints.
      const cfg = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
      if (!isPublicAuthCall && !isOnPublicAuthPage && cfg && !cfg._retried) {
        cfg._retried = true;
        const newToken = await attemptSilentRefresh();
        if (newToken) {
          cfg.headers = {
            ...(cfg.headers || {}),
            Authorization: `Bearer ${newToken}`,
          };
          return api(cfg);
        }
      }

      if (!isPublicAuthCall && !isOnPublicAuthPage) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Shared request config shape for callers that want to opt out of the
// global loading overlay (e.g. setInterval-driven background polls).
// `showLoading: false` is read back off the config object in the
// request interceptor above.
export type RequestOptions = AxiosRequestConfig & { showLoading?: boolean };

export default api;

